import React, { useState, useRef, useCallback } from 'react';
import { Upload, X } from 'lucide-react';
import { formatFileSize } from '../../utils/format';
import { useDropzone, FileRejection } from 'react-dropzone';

interface FileUploaderProps {
  onUploadFile: (file: File) => void;
  onClose: () => void;
}

interface UploadFile {
  id: string;
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
}

export default function FileUploader({ onUploadFile, onClose }: FileUploaderProps) {
  const [files, setFiles] = useState<UploadFile[]>([]);
  
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const newFiles = acceptedFiles.map((file: File) => ({
        id: Math.random().toString(36).substr(2, 9),
        file,
        progress: 0,
        status: 'pending' as const
      }));
      setFiles(prev => [...prev, ...newFiles]);
      for (const file of acceptedFiles) {
        onUploadFile(file);
      }
    }
  }, [onUploadFile]);

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    multiple: false,
    noClick: true,
    noDrag: false
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    const newFiles = selectedFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      progress: 0,
      status: 'pending' as const
    }));
    setFiles(prev => [...prev, ...newFiles]);
    for (const file of selectedFiles) {
      onUploadFile(file);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    open();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={(e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-medium">上传文件</h3>
          <button 
            type="button"
            onClick={() => onClose()} 
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div 
          className="p-6"
          {...getRootProps()}
        >
          <input 
            {...getInputProps()} 
            onChange={handleFileSelect} 
            multiple 
          />
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <Upload className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">
              拖拽文件到此处，或者
              <button
                type="button"
                onClick={handleClick}
                className="text-indigo-600 hover:text-indigo-500 font-medium"
              >
                点击选择文件
              </button>
            </p>
          </div>

          {files.length > 0 && (
            <div className="mt-6 space-y-4">
              {files.map(file => (
                <div key={file.id} className="flex items-center justify-between">
                  <div className="flex-1 pr-4">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900">
                        {file.file.name}
                      </span>
                      <span className="ml-2 text-sm text-gray-500">
                        {formatFileSize(file.file.size)}
                      </span>
                    </div>
                    <div className="mt-1 relative">
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div 
                          className="h-2 bg-indigo-600 rounded-full transition-all"
                          style={{ width: `${file.progress}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  <button className="text-gray-400 hover:text-gray-500">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            className="mr-3 inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
          >
            取消
          </button>
          <button
            type="button"
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
          >
            开始上传
          </button>
        </div>
      </div>
    </div>
  );
}